import { AxiosError } from "axios";
import { LockKeyhole } from "lucide-react";
import { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";
import { useForgotPassword } from "~/api/authentication/authentication";
import passwordBg from "~/assets/onboarding/password.svg";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { ROUTES, storeForgotPasswordEmail, validateEmail } from "~/helpers";
import { trackEvent } from "~/utils/analytics";
import { useAuthStepImage } from "../hooks/useAuthStepImage";

enum ForgotPasswordFormState {
  FORM,
  EMAIL_SENT,
}

interface FormErrors {
  email?: boolean;
}

export const ForgotPasswordForm = () => {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [validationErrors, setValidationErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [formState, setFormState] = useState<ForgotPasswordFormState>(
    ForgotPasswordFormState.FORM
  );
  const { mutateAsync: forgotPassword } = useForgotPassword();

  const resendEmail = async () => {
    trackEvent({ eventName: "app/check_email/resend.button/click" });
    await forgotPassword({ data: { email } });
  };

  const navigate = useNavigate();

  const validateForm = () => {
    const newErrors: FormErrors = {};
    let isValid = true;

    if (!validateEmail(email)) {
      newErrors.email = true;
      isValid = false;
    }

    setValidationErrors(newErrors);
    return isValid;
  };

  useAuthStepImage(passwordBg);

  const navigateToLogin = (isCheckEmail: boolean) => () => {
    trackEvent({
      eventName: isCheckEmail
        ? "app/check_email/log_in.button/click"
        : "app/forgot_password/log_in.button/click",
    });
    navigate(ROUTES.LOGIN);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setError("");
    setIsLoading(true);
    trackEvent({
      eventName: "app/forgot_password/reset_password.button/submit",
    });
    try {
      await forgotPassword({ data: { email } });
      storeForgotPasswordEmail(email);
      setFormState(ForgotPasswordFormState.EMAIL_SENT);
    } catch (error) {
      if (error instanceof AxiosError && error.response?.status === 404) {
        setError("User not found");
      } else if (
        error instanceof AxiosError &&
        error.response?.status === 428
      ) {
        setError(
          "Cannot reset password. Please set your permanent password first"
        );
      } else {
        setError("Failed to send reset code. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (formState === ForgotPasswordFormState.EMAIL_SENT) {
      trackEvent({ eventName: "app/check_email/visit" });
    }
  }, [formState]);

  return (
    <div className="mx-auto max-w-[560px] mt-10">
      <LockKeyhole className="h-[32px] w-[32px] text-[#71717A] mb-[24px]" />
      <h3 className="text-2xl font-semibold text-base-foreground pt-[16px]">
        <FormattedMessage
          id="loginForm.resetPassword"
          defaultMessage="Reset Password"
        />
      </h3>
      {formState === ForgotPasswordFormState.FORM && (
        <>
          <p className="text-sm text-[#71717A] mt-[16px] mb-[24px]">
            <FormattedMessage
              id="loginForm.resetPasswordSubtitle"
              defaultMessage="Enter your email to reset password."
            />
          </p>
          <form onSubmit={handleSubmit}>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={`mt-1 ${validationErrors.email || error ? "border-red-600" : ""}`}
            />
            {validationErrors.email && (
              <span className="text-red-600 text-sm mt-2 block">
                Email should be{" "}
                <strong className="font-semibold">example@site.com</strong>
              </span>
            )}
            {error && (
              <span className="text-red-600 text-sm mt-2 block">{error}</span>
            )}
            <Button
              type="submit"
              className="w-full mt-[24px]"
              disabled={isLoading}
            >
              {isLoading ? "Sending..." : "Reset Password"}
            </Button>
            <div className="w-full grow shrink basis-0 text-center leading-[14px] py-[16px]">
              <span className="text-muted-foreground text-sm font-semibold">
                or
              </span>
            </div>
            <Button
              variant="secondary"
              onClick={navigateToLogin(false)}
              className="w-full"
            >
              Log In
            </Button>
          </form>
        </>
      )}
      {formState === ForgotPasswordFormState.EMAIL_SENT && (
        <div className="flex flex-col gap-6 w-full mt-[16px]">
          <span className="text-sm leading-tight text-muted-foreground">
            We've emailed <b>{email}</b> with a password reset link.
          </span>
          <div className="flex flex-col gap-4">
            <Button className="w-full" onClick={resendEmail}>
              Resend
            </Button>
            <span className="text-[#71717A] text-sm font-medium text-center">
              or
            </span>
            <Button
              className="w-full"
              variant="secondary"
              onClick={navigateToLogin(true)}
            >
              Log In
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
